import { CanActivate, UrlTree } from '@angular/router';
import { UserAuthService } from '../user-auth/user-auth.service';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { GraphInitService } from '../graph/graph-init.service';
import { GraphService } from '../graph/graph.service';
import { UnsavedChangesService } from '../unsaved-changes.service';

@Injectable({ providedIn: 'root' })
export class AuthRouteGuard implements CanActivate {
    constructor(
        private graphService: GraphService,
        private userAuthService: UserAuthService,
        private router: Router,
        private graphInitService: GraphInitService,
        private unsavedChangesService: UnsavedChangesService
    ) { }
    
    async canActivate(): Promise<boolean | UrlTree> {
        const canLeaveCurrentPage = await this.unsavedChangesService.canLeave('route');
        if (!canLeaveCurrentPage) {
            return false;
        }

        if (this.userAuthService.checkMsalAuth()) {
            // Use the new async method to check user existence and handle token refresh
            return new Promise<boolean | UrlTree>((resolve) => {
                this.userAuthService.checkUserExisting().subscribe(
                    async (isValid) => {
                        if (isValid) {
                            try {
                                // Continue with your existing initialization logic
                                const initResult = await this.initializeUserSession();
                                resolve(initResult);
                            } catch (error) {
                                console.error('Initialization failed:', error);
                                resolve(this.router.createUrlTree(['login']));
                            }
                        } else {
                            resolve(this.router.createUrlTree(['login']));
                        }
                    },
                    (error) => {
                        console.error('Auth check failed:', error);
                        resolve(this.router.createUrlTree(['login']));
                    }
                );
            });
        } else {
            return this.router.createUrlTree(['login']);
        }
    }

    private async initializeUserSession(): Promise<boolean> {
        try {
            await this.ensureCommunicationSite();
            await this.ensureSharePointHost();
            return true;
        } catch (error) {
            console.error('Session initialization error:', error);
            return false;
        }
    }

    /**
     * Restore the BluePrint communication site id from the cache. If the cached
     * id is invalid, corrupted or stale (the Graph call fails or returns an
     * unexpected shape), discard it and rebuild from the server instead of
     * letting the error bubble up into a blank screen.
     */
    private async ensureCommunicationSite(): Promise<void> {
        const cachedId = localStorage.getItem('bluePrintCommunicationGroupId');
        if (cachedId != null) {
            try {
                this.graphService.bluePrintCommunicationGroupId = cachedId;
                const data: any = await this.graphService.getBluePrintCommunicationSiteId().toPromise();
                this.graphService.bluePrintCommSiteId = data.parentReference.siteId;
                return;
            } catch (error) {
                console.warn('Cached bluePrintCommunicationGroupId is invalid - rebuilding:', error);
                localStorage.removeItem('bluePrintCommunicationGroupId');
            }
        }
        await this.graphInitService.createCommonCommunicationSite();
    }

    /**
     * Restore the SharePoint host details from the cache. If the cached value is
     * missing required fields or is not valid JSON (corrupted), discard it and
     * re-fetch it from the server.
     */
    private async ensureSharePointHost(): Promise<void> {
        const raw = localStorage.getItem('sharePointHost');
        if (raw != null) {
            try {
                const userDetails = JSON.parse(raw);
                if (userDetails && userDetails.userDomain && userDetails.sharePointHost) {
                    this.graphService.userDomain = userDetails.userDomain;
                    this.graphService.userDetails = userDetails.userDetails;
                    this.graphService.sharePointHost = userDetails.sharePointHost;
                    return;
                }
            } catch (error) {
                console.warn('Cached sharePointHost is corrupted - re-fetching:', error);
            }
            localStorage.removeItem('sharePointHost');
        }
        await this.graphInitService.getSharePointHostDetails();
    }
}
